import React, { useState } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Margin Guard pre-sync check. Returns {proceed, ignore}:
// no violations -> proceed silently; violations -> confirm dialog
// (OK = push anyway below cost, Cancel = abort the sync).
export const checkMarginGuard = async (channel) => {
  try {
    const r = await axios.get(`${API}/price-guard?channel=${channel}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    const v = r.data.violations || [];
    if (!v.length) return { proceed: true, ignore: false };
    const list = v.slice(0, 5).map((x) => `• ${x.sku}: price AED ${x.channel_price} < cost AED ${x.cost}`).join('\n');
    const more = v.length > 5 ? `\n…and ${v.length - 5} more` : '';
    const ok = window.confirm(`⚠️ Margin Guard — ${v.length} product(s) would be pushed BELOW cost on ${channel}:\n\n${list}${more}\n\nOK = push anyway (below cost)\nCancel = abort sync`);
    return { proceed: ok, ignore: ok };
  } catch {
    return { proceed: true, ignore: false };
  }
};

export const computeChannelPrice = (pricing, selling, compare) => {
  const s = parseFloat(selling);
  if (!pricing || !s || s <= 0) return null;
  const c = parseFloat(compare) || 0;
  const mv = parseFloat(pricing.price_markup_value) || 0;
  const mt = pricing.price_markup_type;
  const mark = (x) => (mv > 0 && (mt === 'percent' || mt === 'percentage') ? x * (1 + mv / 100) : (mv > 0 && mt === 'fixed' ? x + mv : x));
  let price = mark(s);
  let ship = 0;
  const th = parseFloat(pricing.shipping_fee_threshold) || 0;
  const amt = parseFloat(pricing.shipping_fee_amount) || 0;
  if (pricing.shipping_fee_enabled && th > 0 && amt > 0 && price < th) ship = amt;
  price = Math.round((price + ship) * 100) / 100;
  let original = null;
  if (c > s) {
    original = Math.max(price, Math.round((mark(c) + ship) * 100) / 100);
  }
  return { price, original, ship };
};

export const ChannelPricingRules = ({ pricing, setPricing, idPrefix, priceLabel = 'Price', compareLabel = 'Original Price', markupPercentValue = 'percent' }) => {
  const [preview, setPreview] = useState({ selling: '40', compare: '60' });
  const pv = computeChannelPrice(pricing, preview.selling, preview.compare);
  if (!pricing) return null;
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm text-wt-text-primary">Price markup</p>
        <p className="text-xs text-wt-text-muted">Increase prices sent to this channel only — your app prices stay unchanged. Applied to both {priceLabel} and {compareLabel}.</p>
        <div className="flex items-center gap-3">
          <Select value={pricing.price_markup_type || 'none'} onValueChange={(v) => setPricing((p) => ({ ...p, price_markup_type: v }))}>
            <SelectTrigger className="w-44" data-testid={`${idPrefix}-markup-type-select`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none" data-testid={`${idPrefix}-markup-none`}>No markup</SelectItem>
              <SelectItem value={markupPercentValue} data-testid={`${idPrefix}-markup-percent`}>Percentage (%)</SelectItem>
              <SelectItem value="fixed" data-testid={`${idPrefix}-markup-fixed`}>Fixed amount (AED)</SelectItem>
            </SelectContent>
          </Select>
          {pricing.price_markup_type && pricing.price_markup_type !== 'none' && (
            <Input type="number" min="0" step="0.01" className="w-32" value={pricing.price_markup_value}
              onChange={(e) => setPricing((p) => ({ ...p, price_markup_value: e.target.value }))}
              placeholder={pricing.price_markup_type === 'fixed' ? 'e.g. 15' : 'e.g. 10'} data-testid={`${idPrefix}-markup-value-input`} />
          )}
        </div>
      </div>

      <div className="border-t border-wt-border pt-4 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm text-wt-text-primary">Add shipping fee to low-priced products</p>
            <p className="text-xs text-wt-text-muted">If the price <b>after markup</b> is below the threshold, a fixed shipping amount is added on top.</p>
          </div>
          <Switch checked={!!pricing.shipping_fee_enabled} onCheckedChange={(v) => setPricing((p) => ({ ...p, shipping_fee_enabled: v }))} data-testid={`${idPrefix}-shipfee-switch`} />
        </div>
        {!!pricing.shipping_fee_enabled && (
          <div className="flex items-center gap-2 text-sm text-wt-text-muted flex-wrap">
            <span>If price below AED</span>
            <Input type="number" min="0" step="0.01" className="w-24" value={pricing.shipping_fee_threshold}
              onChange={(e) => setPricing((p) => ({ ...p, shipping_fee_threshold: e.target.value }))} placeholder="50" data-testid={`${idPrefix}-shipfee-threshold-input`} />
            <span>add AED</span>
            <Input type="number" min="0" step="0.01" className="w-24" value={pricing.shipping_fee_amount}
              onChange={(e) => setPricing((p) => ({ ...p, shipping_fee_amount: e.target.value }))} placeholder="35" data-testid={`${idPrefix}-shipfee-amount-input`} />
          </div>
        )}
      </div>

      <div className="border-t border-wt-border pt-4 space-y-2">
        <p className="text-xs text-wt-text-muted font-medium">Preview with a sample product</p>
        <div className="flex items-center gap-2 text-sm text-wt-text-muted flex-wrap">
          <span>Selling price</span>
          <Input type="number" min="0" className="w-24" value={preview.selling} onChange={(e) => setPreview((v) => ({ ...v, selling: e.target.value }))} data-testid={`${idPrefix}-preview-selling-input`} />
          <span>Compare-at</span>
          <Input type="number" min="0" className="w-24" value={preview.compare} onChange={(e) => setPreview((v) => ({ ...v, compare: e.target.value }))} data-testid={`${idPrefix}-preview-compare-input`} />
        </div>
        {pv && (
          <p className="text-sm" data-testid={`${idPrefix}-pricing-preview`}>
            <span className="text-wt-text-muted">Channel gets → </span>
            {pv.original != null && <span className="text-wt-text-primary font-semibold">{compareLabel} AED {pv.original.toFixed(2)} · </span>}
            <span className="text-wt-gold font-semibold">{priceLabel} AED {pv.price.toFixed(2)}</span>
            {pv.ship > 0 && <span className="text-xs text-wt-text-muted"> (includes AED {pv.ship} shipping)</span>}
          </p>
        )}
      </div>
    </div>
  );
};
